import { APPLICATION_STEPS, APPLICATION_STEPS_WITH_LINES_AND_TITLES } from '@application/constants';
import bannerBackgroundImage from '@assets/banner-background-image.svg';
import bannerImage from '@assets/suggestion-banner-image.webp';
import {
  ApplicationsSuggestionTable,
  Banner,
  Button,
  Container,
  EmptyOverlay,
  HeaderProfile,
  Illustration,
  LoadingContainer,
  Section,
  StepLine,
} from '@presentation/components';
import { BREAKPOINTS } from '@presentation/styles';
import { useTranslation } from '@presentation/utils';
import { Form, FormikProvider } from 'formik';
import type { FC } from 'react';
import { useMemo } from 'react';
import { TbArrowNarrowRight } from 'react-icons/tb';
import { Auth } from './components/Auth';
import { Home } from './components/Home';
import { Preferences } from './components/Preferences';
import { PuppyCare } from './components/PuppyCare';
import { Success } from './components/Success';
import * as Styled from './styled';
import type { ApplicationPageProps } from './types';

export * from './types';

export const ApplicationPage: FC<ApplicationPageProps> = (props) => {
  const {
    onNextClick,
    applicationForm,
    suggestionsForm,
    header,
    currentStep,
    breedOptions,
    auth,
    firstName,
    breederSpokenLanguages,
    suggestionRows,
    suggestionRowsLoading,
    checkoutLoading,
    onStepClick,
    onCheckoutClick,
    onSuggestedBreederClick,
    draftIsSubmitting,
  } = props;
  const { translate } = useTranslation();
  const steps = useMemo(() => {
    return [
      {
        key: APPLICATION_STEPS.FORM,
        title: translate('glossary.application'),
        onStepClick,
      },
      {
        key: APPLICATION_STEPS.LOGIN,
        title: translate('glossary.auth'),
      },
      {
        key: APPLICATION_STEPS.SUGGESTIONS,
        title: translate('glossary.suggestions'),
        onStepClick,
      },

      { key: APPLICATION_STEPS.SUCCESS, title: translate('glossary.confirmation') },
    ];
  }, [onStepClick, translate]);

  const showStepLine = (step: number) => {
    return ![3, 4].includes(currentStep) && APPLICATION_STEPS_WITH_LINES_AND_TITLES.includes(step);
  };

  const stepsWithLines = steps.filter((step) => showStepLine(step.key));

  return (
    <Styled.Wrapper>
      <HeaderProfile {...header} />

      <Styled.TopWrapper marginTop={currentStep === APPLICATION_STEPS.SUCCESS ? 140 : -40}>
        {showStepLine(currentStep) && (
          <Styled.StepLineWrapper>
            <StepLine
              currentStep={currentStep - APPLICATION_STEPS_WITH_LINES_AND_TITLES[0]}
              steps={stepsWithLines}
              color="dark"
              verticalBreakpoint={BREAKPOINTS.tablet}
            />
          </Styled.StepLineWrapper>
        )}

        {currentStep === APPLICATION_STEPS.LOGIN && (
          <Styled.AuthWrapper>
            <Auth {...auth} />
          </Styled.AuthWrapper>
        )}

        {currentStep === APPLICATION_STEPS.SUCCESS && (
          <Success
            firstName={firstName}
            checkoutLoading={checkoutLoading}
            onCheckoutClick={onCheckoutClick}
          />
        )}
      </Styled.TopWrapper>

      {currentStep === APPLICATION_STEPS.FORM && (
        <Styled.Content>
          <FormikProvider value={applicationForm}>
            <Form>
              <Home />
              <PuppyCare />
              <Preferences
                breedOptions={breedOptions}
                breederSpokenLanguages={breederSpokenLanguages}
              />

              <Styled.ButtonWrapper>
                <Button
                  iconRight={<TbArrowNarrowRight />}
                  loading={draftIsSubmitting}
                  color="blue"
                  size="large"
                  shrink
                  onClick={onNextClick}
                >
                  {translate('common.buttons.next')}
                </Button>
              </Styled.ButtonWrapper>
            </Form>
          </FormikProvider>
        </Styled.Content>
      )}

      {currentStep === APPLICATION_STEPS.SUGGESTIONS && (
        <FormikProvider value={suggestionsForm}>
          <Form>
            {suggestionRowsLoading && <LoadingContainer />}
            {!suggestionRowsLoading &&
              (suggestionRows.length > 0 ? (
                <>
                  <Container>
                    <Section>
                      <Banner
                        title={translate('common.banners.suggestions.title')}
                        text={translate('common.banners.suggestions.text')}
                        theme="blue"
                        backgroundImage={bannerBackgroundImage}
                        image={bannerImage}
                      />
                    </Section>

                    <Section>
                      <ApplicationsSuggestionTable
                        rows={suggestionRows}
                        showSkipButton
                        canSubmit={suggestionsForm.values.selectedBreeders.some(
                          (selected: boolean) => !!selected,
                        )}
                        onSuggestedBreederClick={onSuggestedBreederClick}
                      />
                    </Section>
                  </Container>
                </>
              ) : (
                <Styled.Content>
                  <EmptyOverlay
                    wrapperProps={{ style: { minHeight: 'auto' } }}
                    icon={<Illustration type="litter" />}
                    title={translate('common.sections.applicationsSuggestion.empty')}
                  />
                  <Styled.ButtonWrapper>
                    <Button
                      iconRight={<TbArrowNarrowRight />}
                      loading={draftIsSubmitting}
                      color="blue"
                      size="large"
                      shrink
                      type="submit"
                    >
                      {translate('common.buttons.next')}
                    </Button>
                  </Styled.ButtonWrapper>
                </Styled.Content>
              ))}
          </Form>
        </FormikProvider>
      )}
    </Styled.Wrapper>
  );
};
